import { useState, useEffect } from 'react';
import { HiSearch, HiPencil, HiTrash, HiCheck, HiX } from 'react-icons/hi';
import api from '../../utils/api';   // your Axios instance

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add / Edit / Search
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [search, setSearch] = useState('');

  // ---------- Fetch (with cache‑buster) ----------
  const fetchCategories = () => {
    setLoading(true);
    api.get(`/api/categories?_=${Date.now()}`)
      .then(res => {
        // Keep only valid entries
        const valid = res.data.filter(cat => cat.name);
        setCategories(valid);
      })
      .catch(() => setError('Failed to load categories'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ---------- Add ----------
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post('/api/admin/categories', { name: newName.trim() });
      setNewName('');
      fetchCategories();
    } catch (err) {
      alert('Could not add category');
    }
  };

  // ---------- Edit ----------
  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/api/admin/categories/${id}`, { name: editName.trim() });
      setEditingId(null);
      setEditName('');
      fetchCategories();
    } catch (err) {
      alert('Could not update category');
    }
  };

  // ---------- Delete ----------
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/api/admin/categories/${id}`);
      fetchCategories();
    } catch (err) {
      alert('Could not delete category');
    }
  };

  // ---------- Filter by search ----------
  const filtered = categories.filter(cat =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Categories</h2>

      {/* Search + Add row */}
      <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-6">
        <div className="relative flex-1">
          <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-black outline-none"
          />
        </div>
        <form onSubmit={handleAdd} className="flex gap-2">
          <input
            type="text"
            placeholder="New category name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            required
            className="flex-1 sm:w-48 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-black outline-none"
          />
          <button
            type="submit"
            className="bg-black text-white px-5 py-3 rounded-xl font-semibold text-sm hover:bg-gray-800 transition"
          >
            Add
          </button>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 bg-white border border-gray-200 rounded-2xl text-gray-500">
          {search ? 'No matching categories' : 'No categories yet.'}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(category => (
                <tr key={category.id} className="border-t border-gray-100 hover:bg-gray-50/50 transition">
                  {editingId === category.id ? (
                    <>
                      <td className="px-6 py-3">
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-black outline-none"
                        />
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdate(category.id)}
                            className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-700 transition"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-300 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-3 text-gray-900 font-medium">{category.name}</td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEdit(category)}
                            className="text-blue-600 hover:text-blue-800 transition"
                            title="Edit"
                          >
                            <HiPencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(category.id)}
                            className="text-red-500 hover:text-red-700 transition"
                            title="Delete"
                          >
                            <HiTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}